import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { repositorioAlarmas } from '../../data/repositories/RepositorioAlarmas';
import { Criticidad } from '../../domain/value-objects/Criticidad';
import { registrarError } from '../../domain/errores';
import { servicioAvisoSonoro } from '../../services/ServicioAvisoSonoro';
import { useAvisoSonoroStore } from '../stores/avisoSonoroStore';
import { useTiempoReal } from './useTiempoReal';

/** Cuánto se queda en pantalla el aviso, según lo grave que sea. */
const DURACION: Readonly<Record<string, number>> = {
  critica: 15_000,
  alta: 12_000,
  media: 8_000,
  baja: 6_000,
};

function leerCampo(fila: unknown, campo: string): string | null {
  if (typeof fila !== 'object' || fila === null) return null;
  const valor = (fila as Record<string, unknown>)[campo];
  return valor == null ? null : String(valor);
}

/**
 * Avisa cuando entra una alarma nueva, esté donde esté el usuario: suena y
 * aparece un mensaje en pantalla.
 *
 * Se engancha en el armazón de la aplicación, no en la pantalla de alarmas: el
 * sentido de un aviso es enterarse justamente cuando no se está mirando esa
 * pantalla. Por eso mismo, estando ya en ella no se muestra nada —la fila
 * aparece sola y resaltada—, pero el sonido se mantiene.
 *
 * Escucha las dos tablas por separado —alarmas de sensores y de medidores—
 * para que si una no está publicada en tiempo real, la otra siga avisando.
 */
export function useAvisoDeAlarmas(): void {
  const activo = useAvisoSonoroStore((s) => s.activo);
  const navegar = useNavigate();
  const ubicacion = useLocation();

  // En referencias porque el manejador de tiempo real se registra una sola vez
  // y si leyera estos valores directamente se quedaría con los del montaje.
  const refSonido = useRef(activo);
  refSonido.current = activo;

  const refEnAlarmas = useRef(false);
  refEnAlarmas.current = ubicacion.pathname.startsWith('/alarmas');

  /*
   * Una misma alarma puede llegar por las dos tablas, o repetirse si la
   * conexión se restablece y el servidor reenvía. Sin esto el operador vería
   * el mismo aviso dos veces y dejaría de fiarse de ellos.
   */
  const yaAvisadas = useRef(new Set<string>());

  useEffect(() => {
    servicioAvisoSonoro.preparar();
  }, []);

  const avisar = async (fila: unknown, origen: 'sensor' | 'medidor') => {
    const id = leerCampo(fila, 'id');
    if (id) {
      if (yaAvisadas.current.has(id)) return;
      yaAvisadas.current.add(id);
      // Se olvida al cabo de un rato: la memoria no debe crecer sin fin en una
      // pantalla que se deja abierta durante días.
      setTimeout(() => yaAvisadas.current.delete(id), 5 * 60_000);
    }

    const criticidad = Criticidad.from(leerCampo(fila, 'criticidad'));

    if (refSonido.current) servicioAvisoSonoro.avisar(criticidad);
    if (refEnAlarmas.current) return;

    /*
     * La fila que llega en tiempo real es la cruda: trae el identificador del
     * sensor pero no su nombre, y «Alarma en el sensor 47» no le dice nada a
     * nadie. La vista sí los trae, y además aplica el alcance de la sesión, así
     * que una alarma de otro cliente devuelve nada y no se avisa de ella.
     */
    let titulo = origen === 'medidor' ? 'Alarma de un medidor' : 'Alarma nueva';
    let detalle = leerCampo(fila, 'mensaje') ?? '';

    if (origen === 'sensor' && id) {
      try {
        const alarma = await repositorioAlarmas.obtenerPorId(id);
        if (!alarma) return;
        titulo = `${alarma.criticidad.etiqueta}: ${alarma.sensorNombre}`;
        detalle = [alarma.mensaje, alarma.areaNombre].filter(Boolean).join(' · ');
      } catch (e) {
        // Sin los nombres el aviso pierde calidad, pero callarlo sería peor:
        // se muestra con lo que trae la fila cruda.
        registrarError('avisoAlarmas.detalle', e);
      }
    }

    const mostrar =
      criticidad.valor === 'critica' || criticidad.valor === 'alta' ? toast.error : toast.warning;

    mostrar(titulo, {
      description: detalle || undefined,
      duration: DURACION[criticidad.valor] ?? 8_000,
      action: { label: 'Ver', onClick: () => navegar('/alarmas') },
    });
  };

  useTiempoReal({ tabla: 'alarmas', evento: 'INSERT' }, (payload) => {
    void avisar(payload.new, 'sensor');
  });

  useTiempoReal({ tabla: 'smart_meter_alarms', evento: 'INSERT' }, (payload) => {
    void avisar(payload.new, 'medidor');
  });
}
